import { PimSupplierProductArticleNumberInput } from "../../../clients/graphqlTypes.js";
import {
  AddProductPreferenceResultFragment,
  ProductPreferenceFragment,
} from "./ProductPreferences.generated.js";

/**
 * The single product type a preference can be keyed to, or `null` when the filters leave it
 * ambiguous. A preference is stored per `(article, product type, scope)`, so inferring a
 * product type from anything other than an unambiguous filter would silently write a
 * preference the user never asked for.
 */
export function getProductTypeInContext(
  filters: { productTypeIds?: string[] | null } | null | undefined
): string | null {
  const [only, ...rest] = filters?.productTypeIds ?? [];
  return rest.length === 0 && only !== undefined ? only : null;
}

/**
 * The product type a preference written from the product search is keyed to.
 *
 * A pinned type wins over the filtered one: where the caller opened the search to work on one
 * product type, widening or clearing the product type filter is how the user reaches articles
 * the catalogue has not classified as it yet — so the filter must be free to move without
 * moving what the preference is written against.
 */
export function getPreferenceProductType(
  pinnedProductTypeId: string | null,
  filters: { productTypeIds?: string[] | null } | null | undefined
): string | null {
  return pinnedProductTypeId ?? getProductTypeInContext(filters);
}

export function articleNumberIdentifierKey(identifier: {
  supplierId: string;
  articleNumber: string;
}): string {
  return `${identifier.supplierId}/${identifier.articleNumber}`;
}

export type ProductPreferenceScopes = {
  user: boolean;
  organisation: boolean;
};

export const NO_PREFERENCE: ProductPreferenceScopes = {
  user: false,
  organisation: false,
};

/**
 * Preferred state per article, keyed by `(supplierId, articleNumber)`. A hit's `preferences`
 * attribution is keyed to the search's context type and carries no scope, so the UI derives
 * the toggle state by matching the preference list instead.
 */
export function indexPreferencesByArticle(
  preferences: readonly ProductPreferenceFragment[]
): Map<string, ProductPreferenceScopes> {
  const byArticle = new Map<string, ProductPreferenceScopes>();

  for (const preference of preferences) {
    const key = articleNumberIdentifierKey(preference.articleNumberIdentifier);
    const current = byArticle.get(key) ?? { ...NO_PREFERENCE };
    byArticle.set(key, {
      user: current.user || preference.scope === "USER",
      organisation: current.organisation || preference.scope === "ORGANISATION",
    });
  }

  return byArticle;
}

export type AddProductPreferenceSummary = {
  added: PimSupplierProductArticleNumberInput[];
  conflicts: {
    articleNumber: string;
    supplierId: string;
    productTypeKey: string;
    /** The label when PIM resolves the product type, else its key. */
    productTypeName: string;
    isAuthoritative: boolean;
  }[];
  unknownSuppliers: PimSupplierProductArticleNumberInput[];
};

/**
 * Splits a bulk add's per-article verdicts into the three outcomes the user has to be told
 * apart. A conflict is not a generic failure: it means PIM disagrees with the user about what
 * the article *is*, and it names the classification that blocked it — which is the only thing
 * that makes the rejection actionable.
 */
export function summariseAddProductPreferenceResults(
  results: readonly AddProductPreferenceResultFragment[]
): AddProductPreferenceSummary {
  const summary: AddProductPreferenceSummary = {
    added: [],
    conflicts: [],
    unknownSuppliers: [],
  };

  for (const result of results) {
    const { supplierId, articleNumber } = result.articleNumberIdentifier;
    switch (result.status) {
      case "ADDED":
        summary.added.push({ supplierId, articleNumber });
        break;
      case "PRODUCT_TYPE_CONFLICT":
        summary.conflicts.push({
          supplierId,
          articleNumber,
          productTypeKey: result.conflictingProductType?.productTypeKey ?? "",
          productTypeName:
            result.conflictingProductType?.label ??
            result.conflictingProductType?.productTypeKey ??
            "",
          isAuthoritative:
            result.conflictingProductType?.source === "AUTHORITATIVE",
        });
        break;
      case "UNKNOWN_SUPPLIER":
        summary.unknownSuppliers.push({ supplierId, articleNumber });
        break;
    }
  }

  return summary;
}

export function isPartialSuccess(
  summary: AddProductPreferenceSummary
): boolean {
  return (
    summary.added.length > 0 &&
    summary.conflicts.length + summary.unknownSuppliers.length > 0
  );
}
